import Baseball from "./standings/Baseball";
import Soccer from "./standings/Soccer";
import Rugby from "./standings/Rugby";
import Hockey from "./standings/Hockey";
import GetStanding from "../../components/season/queries/GetStanding";
import React, { useEffect, useMemo, useState } from "react";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

export default function Standings({ division, season }) {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const [getStanding] = GetStanding();
    const [standingByGroup, setStandingByGroup] = useState([]);
    const [loading, setLoading] = useState(false);

    const scopeId = useMemo(() => {
        const safe = `standings-${division?.sport?.key || "sport"}-${season?.id || "x"}`
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `kbola-${safe}`;
    }, [division?.sport?.key, season?.id]);

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            body: { padding: 14 },
            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            loadingRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "18px 10px",
                borderRadius: 18,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
            },
            pill: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 999,
                fontWeight: 900,
                fontSize: 12.5,
                border: "1px solid rgba(0,0,0,0.10)",
                lineHeight: 1,
            },
            pillInfo: {
                background: "rgba(13,110,253,0.10)",
                color: "rgba(13,110,253,0.95)",
                borderColor: "rgba(13,110,253,0.18)",
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    useEffect(() => {
        if (!season?.id) return;
        let cancelled = false;

        async function fetchData() {
            setLoading(true);
            try {
                const res = await getStanding({ variables: { seasonId: season.id } });
                if (!cancelled) setStandingByGroup(res.data?.standings || []);
            } catch (error) {
                if (!cancelled) console.error(error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchData();
        return () => {
            cancelled = true;
        };
    }, [getStanding, season?.id]);

    const sportKey = division?.sport?.key;

    const renderStanding = (standing) => {
        const commonProps = {
            groupName: standing.groupName,
            stageName: standing.stageName,
            standings: standing.standings,
        };

        if (sportKey === "baseball" || sportKey === "softball") return <Baseball key={standing.groupId} {...commonProps} />;
        if (sportKey === "hockey") return <Hockey key={standing.groupId} {...commonProps} />;
        if (sportKey === "soccer") return <Soccer key={standing.groupId} {...commonProps} />;
        if (sportKey === "american-football") return <Soccer key={standing.groupId} {...commonProps} />;
        if (sportKey === "rugby") return <Rugby key={standing.groupId} {...commonProps} />;
        return <Soccer key={standing.groupId} {...commonProps} />;
    };

    const count = standingByGroup?.length || 0;

    return (
        <div id={scopeId} className="kbola-standings">
            <div style={ui.wrap}>
                <div style={ui.header}>
                    <div style={ui.titleWrap}>
                        <span style={ui.badge}>{count}</span>
                        <div style={{ minWidth: 0 }}>
                            <h3 style={ui.title}>{t("standings.title")}</h3>
                            <p style={ui.sub}>
                                {division?.name
                                    ? t("standings.seasonStandingsFor", { division: division.name })
                                    : t("standings.seasonStandings")}
                            </p>
                        </div>
                    </div>
                </div>

                <div style={ui.body}>
                    {loading && (
                        <div
                            style={ui.loadingRow}
                            role="status"
                            aria-label={t("standings.loadingAria")}
                        >
                            <div className="spinner-border" style={{ width: 22, height: 22 }} />
                            <div style={{ fontWeight: 900, color: "rgba(20,21,26,0.70)" }}>
                                {t("standings.loading")}
                            </div>
                        </div>
                    )}

                    {!loading && standingByGroup.length === 0 && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <span className="kbola-pill" style={{ ...ui.pill, ...ui.pillInfo }}>
                                <i className="fas fa-info-circle" />
                                {t("standings.empty")}
                            </span>
                        </div>
                    )}

                    {!loading && standingByGroup.length > 0 && (
                        <div className="kbola-standingsList">{standingByGroup.map(renderStanding)}</div>
                    )}
                </div>
            </div>

            <style>{`
        #${scopeId} {
          font-family: 'Poppins', sans-serif;
        }

        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        #${scopeId} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }

        #${scopeId} .kbola-standingsList {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        #${scopeId} .kbola-table thead th,
        #${scopeId} table.kbola-table thead th,
        #${scopeId} table.table thead th,
        #${scopeId} table.table thead.thead-light th {
          background: rgba(0,0,0,0.02);
          border-bottom: 1px solid rgba(0,0,0,0.06);
          color: rgba(20,21,26,0.72);
          font-size: 12.5px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          padding: 12px 14px;
        }

        #${scopeId} .kbola-table tbody td,
        #${scopeId} table.kbola-table tbody td,
        #${scopeId} table.table tbody td {
          border-top: 1px solid rgba(0,0,0,0.06);
          padding: 12px 14px;
          vertical-align: middle;
          color: rgba(20,21,26,0.86);
          font-weight: 700;
          font-size: 13.5px;
        }

        #${scopeId} .kbola-table tbody tr:hover,
        #${scopeId} table.table tbody tr:hover {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
        }

        #${scopeId} button:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        @media (max-width: 576px) {
          #${scopeId} .kbola-pill {
            white-space: normal;
            text-align: center;
            justify-content: center;
          }
        }
      `}</style>
        </div>
    );
}
